<template>
  <form
    ref="formRefundTypeStep"
    @submit.prevent="$emit('submit', convertedValues)"
  >
    <b>
      <emd-text size="medium">
        {{ $t('title') }}
      </emd-text>
    </b>

    <div class="radios eu-mt_4">
      <emd-radio
        v-model="values.refundType"
        color="primary"
        name="refundType"
        :value="refundTypeTotal"
        :label="$t('total')"
        data-testid="refundTypeTotal"
      />

      <emd-radio
        v-model="values.refundType"
        color="primary"
        name="refundType"
        :value="refundTypePartial"
        :label="$t('partial')"
        data-testid="partial"
      />
    </div>

    <div class="flex eu-mt_4 eu-mb_5">
      <div
        class="flex-group"
        v-if="shouldDisplaySplitsSelect"
      >
        <label
          class="emd-label _required"
          for="split"
        >
          {{ $t('split') }}
        </label>
        <emd-select
          data-testid="split"
          v-model="values.split"
          name="split"
        >
          <option :value="SPLIT_ENUM.ALL">
            {{ $t('all') }}
          </option>
          <option
            v-for="receiver in initialChargeSplit"
            :key="receiver.recipient.id"
            :value="receiver.recipient.id"
          >
            {{ getRecipientNameBasedOnSplitId(receiver.recipient.id) }}
          </option>
        </emd-select>
      </div>

      <div
        class="flex-group"
        v-if="isRefundTypePartial"
      >
        <label
          class="emd-label _required"
          for="amount"
        >
          {{ $t('amount') }}
        </label>
        <money
          :class="amountClass"
          v-bind="money"
          v-model="values.amount"
          name="amount"
          data-testid="amount"
        />
        <b
          ><span
            v-if="shouldDisplayAmountError"
            class="emd-text _size_small error eu-mt_1"
            >{{
              $t('amountError', { number: convertedTotalAmountCurrency })
            }}</span
          ></b
        >
      </div>
    </div>
    <div
      v-if="$slots.default"
      class="actions"
    >
      <slot />
    </div>
  </form>
</template>
<script>
import { Money } from 'v-money'
import { requiredIf } from 'vuelidate/lib/validators'
import { EmdRadio, EmdSelect, EmdText } from 'emerald-vue/src/components'
import { isReceiversTypeSplit } from '../enums/receivers-type.enum'
import {
  REFUND_TYPE_ENUM,
  isRefundTypePartial
} from '../enums/refund-type.enum'

import { SPLIT_ENUM } from '../enums/splits.enum'

import RefundTypeManager from './managers/refund-type-step.manager'

export default {
  name: 'RefundTypeStep',
  components: {
    EmdRadio,
    EmdSelect,
    EmdText,
    Money
  },
  props: {
    initialValues: Object,
    totalAmount: Number,
    initialChargeSplit: Array
  },
  data () {
    return {
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        suffix: '',
        precision: 2,
        masked: false
      },
      values: {
        amount: 0,
        refundType: REFUND_TYPE_ENUM.TOTAL,
        split: SPLIT_ENUM.ALL // SPLIT_ENUM.ALL | Array of recipient ids | single id
      }
    }
  },
  validations () {
    return {
      values: {
        split: requiredIf(() => this.shouldDisplaySplitsSelect),
        amount: {
          requiredIf: requiredIf(() => this.isRefundTypePartial),
          shouldBeLessOrEqualThanTotalAmount: () =>
            this.computedAmount <= this.totalAmount
        }
      }
    }
  },
  computed: {
    data () {
      return {
        initialChargeSplit: this.initialChargeSplit,
        totalAmount: this.totalAmount,
        amount: this.values.amount,
        refundType: this.values.refundType,
        split: this.values.split
      }
    },
    computedAmount () {
      return this.values.amount * 100
    },
    SPLIT_ENUM () {
      return SPLIT_ENUM
    },
    // For radio TOTAL
    refundTypeTotal () {
      return REFUND_TYPE_ENUM.TOTAL
    },
    // For radio PARTIAL
    refundTypePartial () {
      return REFUND_TYPE_ENUM.PARTIAL
    },
    shouldDisplaySplitsSelect () {
      return new RefundTypeManager({
        initialChargeSplit: this.data.initialChargeSplit,
        refundType: this.data.refundType
      }).shouldDisplaySplitsSelect()
    },
    receiversType () {
      return new RefundTypeManager({
        initialChargeSplit: this.data.initialChargeSplit
      }).receiversType()
    },
    isRefundTypePartial () {
      return isRefundTypePartial(this.data.refundType)
    },
    isReceiversTypeSplit () {
      return isReceiversTypeSplit(this.receiversType)
    },
    convertedValues () {
      return new RefundTypeManager(this.data).prepareDataForSubmission()
    },
    shouldDisplayAmountError () {
      return new RefundTypeManager({
        amount: this.computedAmount,
        totalAmount: this.data.totalAmount
      }).shouldDisplayError()
    },
    amountClass () {
      return {
        'emd-input': true,
        _state_error: this.shouldDisplayAmountError
      }
    },
    convertedTotalAmountCurrency () {
      return new RefundTypeManager({
        totalAmount: this.totalAmount
      }).convertedTotalAmountCurrency()
    }
  },
  methods: {
    getRecipientNameBasedOnSplitId (recipientId) {
      return new RefundTypeManager({
        initialChargeSplit: this.data.initialChargeSplit
      }).getRecipientNameBasedOnSplitId(recipientId)
    }
  },
  watch: {
    '$v.$invalid': {
      handler (value) {
        this.$emit('update:isInvalid', value)
      },
      deep: true,
      immediate: true
    }
  },
  created () {
    this.values = new RefundTypeManager({
      initialValues: this.initialValues
    }).prepareInitialValues()
  }
}
</script>
<style scoped>
.actions {
  display: flex;
  flex-direction: row-reverse;
  gap: 8px;
}
.radios {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
}
.error {
  color: #a71a44;
}
.emd-radio-button__area::after {
  background-color: var(--color-green);
}
.flex {
  display: flex;
  align-items: center;
  gap: 16px;
}

.flex-group {
  display: flex;
  flex-direction: column;
  flex: 1;
}
</style>
<i18n>
  {
    "pt-BR": {
      "title": "De que maneira deseja cancelar essa cobrança?",
      "total": "Estorno total",
      "partial": "Estorno parcial",
      "receiver": "Recebedor {number}",
      "amount": "Valor",
      "amountError": "O valor a estornar é maior que o valor pago (R$ {number}).",
      "all": "Manter a regra de divisão",
      "split": "Recebedor"
    },
    "en-US": {
      "title": "In wich way would you like to cancel?",
      "total": "Total Refund",
      "partial": "Partial Refund",
      "receiver": "Receiver {number}",
      "amount": "Amount",
      "amountError": "The value to refund is larger than the total amount (R$ {number})",
      "all": "Preserve the division rule",
      "split": "Receiver"
    }
  }
</i18n>
